import { IconRefresh } from "@tabler/icons-react";
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAppUpdateStore } from "@/stores/app-update";
import { useSupportWidgetObstruction } from "@/stores/support-widget";

/**
 * Snackbar-баннер «Доступна новая версия» — нижний правый угол.
 *
 * Появляется когда `useAppVersion` обнаруживает изменение sha.
 * Поведение:
 *  - `forceReload=false` (обычный апдейт) — пользователь видит баннер с
 *    кнопкой «Обновить». Дополнительно: на любой route-навигации делаем
 *    автоматический `location.reload()` без подтверждения — момент перехода
 *    самый безопасный (нет несохранённых данных в форме чата/материала).
 *  - `forceReload=true` (критический апдейт) — баннер без кнопки, авто-reload
 *    через 5 секунд. Используется для security/schema fix.
 */

const FORCE_RELOAD_DELAY_MS = 5_000;

function reloadNow() {
	// Hard reload игнорирует HTTP-кэш для index.html — гарантирует подгрузку
	// нового бандла даже если Caddy-кэш где-то залип.
	window.location.reload();
}

export function UpdateBanner() {
	const { t } = useTranslation("common");
	const available = useAppUpdateStore((s) => s.available);
	const forceReload = useAppUpdateStore((s) => s.forceReload);
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const initialPathRef = useRef<string | null>(null);
	// Баннер живёт в правом нижнем углу — там же, где кнопка «Поддержка».
	useSupportWidgetObstruction(available);

	// Запоминаем pathname на момент появления баннера. Любая последующая
	// смена pathname → soft-reload (без подтверждения, апдейт «бесшовный»).
	useEffect(() => {
		if (!available) {
			initialPathRef.current = null;
			return;
		}
		if (initialPathRef.current == null) {
			initialPathRef.current = pathname;
			return;
		}
		if (forceReload) return; // Force-режим обработается отдельным таймером.
		if (pathname !== initialPathRef.current) {
			reloadNow();
		}
	}, [available, forceReload, pathname]);

	// Force reload — таймер 5 секунд после появления баннера.
	useEffect(() => {
		if (!available || !forceReload) return;
		const timer = setTimeout(reloadNow, FORCE_RELOAD_DELAY_MS);
		return () => {
			clearTimeout(timer);
		};
	}, [available, forceReload]);

	if (!available) return null;

	const label = forceReload
		? t("appUpdate.forced", "Updating the app…")
		: t("appUpdate.available", "A new version is available");

	// Слой нижнего хрома (`--sh-z-chrome-top`, 40), а НЕ поверх всего. Было
	// `z-[60]` — выше любого оверлея ui-кита (50): баннер «доступна новая версия»
	// рисовался ПОВЕРХ открытой модалки и перекрывал её кнопки. Уведомление не
	// срочное (обновление приедет на первой же навигации), перебивать диалог
	// ему незачем.
	return (
		<div
			data-testid="update-banner"
			data-banner-variant={forceReload ? "force" : "soft"}
			role="status"
			aria-live="polite"
			className="pointer-events-auto fixed right-4 z-(--sh-z-chrome-top) flex items-center gap-3 rounded-2xl border border-primary/30 bg-primary/15 px-4 py-3 text-sm text-primary shadow-xl shadow-primary/20 backdrop-blur-xl backdrop-saturate-150 bottom-[calc(4rem+env(safe-area-inset-bottom))] sm:bottom-4"
		>
			<IconRefresh className="h-4 w-4 shrink-0" aria-hidden />
			<span className="truncate">{label}</span>
			{!forceReload && (
				<button
					type="button"
					onClick={reloadNow}
					className="ml-1 shrink-0 cursor-pointer rounded-full bg-primary/25 px-3 py-1 font-medium text-xs transition-colors hover:bg-primary/35"
				>
					{t("appUpdate.action", "Update")}
				</button>
			)}
		</div>
	);
}
